import { Card, CardBody, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row, Spinner } from "reactstrap";
import {
  IoCartOutline,
  IoWalletOutline,
  IoAlertCircleOutline,
  IoCalendarOutline,
} from "react-icons/io5";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { endOfMonth, endOfYear, format, startOfMonth, startOfYear, subMonths, subYears } from "date-fns";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { get } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import StatCard from "../../../../components/Cards/StatCard";
import i18n from "../../../../i18n";
import useCurrency from "../../../../utils/helpers/currency";

const DATE_RANGE_OPTIONS = ["this_month", "last_month", "this_year", "last_year", "custom"];

function getDateRange(option, customStart, customEnd) {
  const now = new Date();
  switch (option) {
    case "last_month": {
      const d = subMonths(now, 1);
      return [startOfMonth(d), endOfMonth(d)];
    }
    case "this_year":
      return [startOfYear(now), endOfYear(now)];
    case "last_year": {
      const d = subYears(now, 1);
      return [startOfYear(d), endOfYear(d)];
    }
    case "custom":
      return [customStart, customEnd];
    case "this_month":
    default:
      return [startOfMonth(now), endOfMonth(now)];
  }
}

const PIPELINE_COLORS = {
  Draft: "#adb5bd",
  Sent: "#6C63FF",
  Approved: "#20c997",
  Received: "#4dabf7",
  Canceled: "#fa5252",
};

const tooltipStyle = { fontSize: "0.78rem", borderRadius: 8, border: "1px solid #eee", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" };

function SpendingBarChart({ data, t }) {
  return (
    <div style={{ flex: 1, minHeight: 250 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 8, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: "#555" }}
            axisLine={false}
            tickLine={false}
            reversed={i18n.language === "ar"}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#555" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            width={50}
          />
          <Tooltip
            formatter={(value) => [`$${value.toLocaleString()}`, t("crm.spending")]}
            contentStyle={tooltipStyle}
            cursor={{ fill: "#f0fff4" }}
          />
          <Bar dataKey="value" fill="#20c997" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function OrderPipelineChart({ data }) {
  return (
    <PieChart width={120} height={120}>
      <Pie
        data={data}
        dataKey="count"
        nameKey="stage"
        cx={60}
        cy={60}
        innerRadius={30}
        outerRadius={52}
        paddingAngle={2}
      >
        {data.map((entry, i) => (
          <Cell key={i} fill={PIPELINE_COLORS[entry.stage]} />
        ))}
      </Pie>
      <Tooltip
        formatter={(value, name) => [value, name]}
        contentStyle={tooltipStyle}
      />
    </PieChart>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const { formatDate, datePickerFormat } = useSettings();
  const currency = useCurrency();
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [topSuppliersData, setTopSuppliersData] = useState([]);
  const [monthlySpending, setMonthlySpending] = useState([]);
  const [orderPipeline, setOrderPipeline] = useState([]);
  const [dateRangeOption, setDateRangeOption] = useState("this_month");
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedRange, setAppliedRange] = useState(() => getDateRange("this_month"));
  const [kpiData, setKpiData] = useState({
    spendingThisMonth: 0,
    billsIssued: 0,
    overdueBalance: 0,
  });

  const getHomeData = async (range) => {
    setLoading(true);

    const [from, to] = range;
    const params = new URLSearchParams();
    if (from) params.append("date_from", format(from, "yyyy-MM-dd"));
    if (to) params.append("date_to", format(to, "yyyy-MM-dd"));

    const data = await get(`/purchases/home?${params.toString()}`);
    if (data.success) {
      setTopSuppliersData(data.top_suppliers);
      setMonthlySpending(data.monthly_spending);
      setOrderPipeline(data.order_pipeline);
      setKpiData({
        spendingThisMonth: data.spending_this_month,
        billsIssued: data.bills_issued_this_month,
        overdueBalance: data.bills_overdue_this_month,
      });
    }

    setLoading(false);
  };

  const applyRange = (range) => {
    setAppliedRange(range);
    const [from, to] = range;
    if (from && to) {
      setSearchParams({
        start_date: format(from, "yyyy-MM-dd"),
        end_date: format(to, "yyyy-MM-dd"),
      });
    }
  };

  const selectDateRangeOption = (option) => {
    setDateRangeOption(option);
    if (option === "custom") return;
    applyRange(getDateRange(option));
    setDateDropdownOpen(false);
  };

  const applyCustomRange = () => {
    if (!startDate || !endDate) return;
    applyRange([startDate, endDate]);
    setDateDropdownOpen(false);
  };

  const dateRangeLabel = dateRangeOption === "custom"
    ? (appliedRange[0] && appliedRange[1] ? `${formatDate(appliedRange[0])} - ${formatDate(appliedRange[1])}` : t("common.custom"))
    : t(`common.${dateRangeOption.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`);

  useEffect(() => {
    getHomeData(appliedRange);
  }, [appliedRange]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t("purchases.title")} onReload={() => getHomeData(appliedRange)}>
          <Dropdown isOpen={dateDropdownOpen} toggle={() => setDateDropdownOpen(o => !o)} size="sm">
            <DropdownToggle outline color="light" caret>
              <IoCalendarOutline className="me-1" />
              {dateRangeLabel}
            </DropdownToggle>
            <DropdownMenu end>
              {DATE_RANGE_OPTIONS.filter(o => o !== "custom").map(option => (
                <DropdownItem
                  key={option}
                  active={dateRangeOption === option}
                  onClick={() => selectDateRangeOption(option)}
                >
                  {t(`common.${option.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}
                </DropdownItem>
              ))}
              <DropdownItem divider />
              <DropdownItem
                active={dateRangeOption === "custom"}
                toggle={false}
                onClick={() => setDateRangeOption("custom")}
              >
                {t('common.custom')}
              </DropdownItem>
              {dateRangeOption === "custom" && (
                <div className="px-3 py-2 d-flex flex-column gap-2" style={{ minWidth: 220 }}>
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{t('common.from')}</div>
                    <DatePicker
                      selected={startDate}
                      onChange={setStartDate}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={endDate || new Date()}
                      className="form-control form-control-sm"
                      dateFormat={datePickerFormat}
                      placeholderText={t('common.from')}
                    />
                  </div>
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{t('common.to')}</div>
                    <DatePicker
                      selected={endDate}
                      onChange={setEndDate}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      maxDate={new Date()}
                      className="form-control form-control-sm"
                      dateFormat={datePickerFormat}
                      placeholderText={t('common.to')}
                    />
                  </div>
                  <DefaultButton size="sm" disabled={!startDate || !endDate} onClick={applyCustomRange}>
                    {t('common.apply')}
                  </DefaultButton>
                </div>
              )}
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        {loading ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        :
          <div className="subheader-page-content scrollable">
            {/* KPI Cards */}
            <Row className="g-3 mb-4">
              <Col md="4">
                <StatCard
                  icon={<IoWalletOutline size={18} color="#20c997" />}
                  iconBg="#e6faf5"
                  value={`${kpiData.spendingThisMonth.toLocaleString()} ${currency}`}
                  label={t("crm.spending")}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoCartOutline size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={kpiData.billsIssued}
                  label={t("purchases.bills")}
                />
              </Col>
              <Col md="4">
                <StatCard
                  icon={<IoAlertCircleOutline size={18} color="#fa5252" />}
                  iconBg="#fff0f0"
                  value={`${kpiData.overdueBalance.toLocaleString()} ${currency}`}
                  label={t("common.overdue")}
                />
              </Col>
            </Row>

            {/* Monthly Spending Chart */}
            <Row className="mb-4" style={{ alignItems: "stretch" }}>
              <Col lg="12" className="d-flex flex-column">
                <h6 className="fw-bold">{t("crm.spending")}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-baseline mb-3">
                      <span className="fw-semibold" style={{ fontSize: "0.82rem", color: "#555" }}>
                        {dateRangeLabel}
                      </span>
                    </div>
                    <SpendingBarChart data={monthlySpending} t={t} />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Top Suppliers + Order Pipeline */}
            <Row className="g-3" style={{ alignItems: "stretch" }}>
              <Col lg="6" className="d-flex flex-column">
                <h6 className="fw-bold">{t("crm.topSuppliers")}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="p-0">
                    {topSuppliersData?.length > 0 && topSuppliersData?.map((s, i) => (
                      <div key={s.id} className="sales-panel-item d-flex align-items-center gap-3 px-3 py-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{ width: 32, height: 32, background: "#e6faf5", fontSize: "0.75rem", fontWeight: 700, color: "#20c997" }}
                        >
                          {i + 1}
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-truncate" style={{ fontSize: "0.84rem" }}>
                            {s.company_name || s.firstname + ' ' + s.lastname}
                          </div>
                        </div>
                        <div className="fw-bold" style={{ fontSize: "0.84rem" }}>
                          {parseFloat(s.total_payment_amount).toLocaleString()} {currency}
                        </div>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="6" className="d-flex flex-column">
                <h6 className="fw-bold">{t("sales.orderPipeline")}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex align-items-center gap-4 h-100">
                    <OrderPipelineChart data={orderPipeline} />
                    <div className="sales-donut-legend flex-grow-1">
                      {orderPipeline.map((q) => {
                        const total = orderPipeline.reduce((s, x) => s + x.count, 0);
                        return (
                          <div key={q.stage} className="sales-donut-legend-item">
                            <div className="sales-donut-dot" style={{ background: PIPELINE_COLORS[q.stage] }} />
                            <span className="flex-grow-1">{q.stage}</span>
                            <span className="fw-semibold">{q.count}</span>
                            <span className="text-muted ms-2" style={{ fontSize: "0.72rem" }}>
                              {total > 0 ? Math.round((q.count / total) * 100) : 0}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        }
      </div>
    </SidebarLayout>
  );
}
